import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  getPageInfo,
  getPersonalCharactersDetails,
  getUserWclGuilds,
  getUserTeamAccess,
  getUserTeamPreferences,
} from '../api/user'
import { getRealms } from '../api/realm'
import {
  getGuildDetails,
  getGuildDetailsByPath,
  getGuildMembers,
} from '../api/guild'
import {
  getAccessRestrictions,
  getAuditData,
  getCharacterWishlist,
  getDiscordReferences,
  getNewsItem,
  getNewsItems,
  getTeamDetails,
  getTeamDetailsByPath,
  getTeamDroptimizerConfigurations,
  EventStatisticsQuery,
  getTeamEventStatistics,
  getTeamLootHistory,
  getTeamRefreshStatus,
  getTeamTrackingRules,
  getTeamUsers,
  getTeamWishlist,
  LootHistoryUrlOptions,
} from '../api/team'
import {
  getRaidPlan,
  getRaidPlans,
  getRaidPlansIndex,
  getRaidPlanTopExecutionKills,
  RaidPlanOwner,
  UserRaidPlansIndex,
} from '../api/raid_plans'
import {
  EventActivitiesPlanningInfo,
  EventChangelogEntry,
  getEventRaidReports,
  getEventActivityPlanningKey,
  getTeamEventRaidReportsMap,
  getTeamCalendarEvents,
  getUserListEvents,
  getTeamListEvents,
  getEventActivitiesPlanningInfo,
  getTeamOverviewEvents,
  getTeamEventChangelog,
  getTeamEvent,
  TeamEventRaidReportsMapResponse,
  TeamListEventsResponse,
  TeamOverviewEventsResponse,
} from '../api/event'
import {
  getDiscordChannelDetails,
  getNotifications,
  getOptions,
  getTemplateBlacklists,
  getTemplates,
} from '../api/notification'
import { getOverviewData } from '../api/team'
import { getInstances } from '../api/instance'
import { getCharacterDetails } from '../api/character'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import {
  getTeamApplicationDetails,
  getApplicationMessages,
  getTeamApplications,
  getUserApplications,
  getUserApplicationDetails,
  getUserApplicationSummary,
} from '../api/application'
import useReferenceStore from '../state/useReferenceStore'
import useBasePath from '../hooks/useBasePath'
import usePrefetchedStore from '../state/usePrefetchedStore'
import { useCustomDomain } from '../hooks/useCustomDomain'

export const usePageInfo = () => {
  return useQuery({
    queryKey: ['pageInfo'],
    queryFn: getPageInfo,
    staleTime: 1000 * 60 * 15,
    initialData: window.page_info,
  })
}

export const useUserWclGuilds = (enabled: boolean) => {
  return useQuery({
    queryKey: ['userWclGuilds'],
    queryFn: getUserWclGuilds,
    enabled,
    staleTime: 1000 * 60 * 5,
  })
}

export const useRealms = () => {
  return useQuery({
    queryKey: ['realms'],
    queryFn: getRealms,
    staleTime: 1000 * 60 * 60,
  })
}

export const useInstances = () => {
  return useQuery({
    queryKey: ['instances'],
    queryFn: getInstances,
    staleTime: 1000 * 60 * 60,
  })
}

export const useNotificationTemplateOptions = () => {
  return useQuery({
    queryKey: ['notificationTemplateOptions'],
    queryFn: getOptions,
    staleTime: Infinity,
  })
}

export const useNotificationTemplates = (teamId: number) => {
  return useQuery({
    queryKey: ['notificationTemplates', { id: teamId }],
    queryFn: () => getTemplates(teamId),
    staleTime: 1000 * 60 * 60,
  })
}

export const useNotificationTemplateBlacklists = () => {
  return useQuery({
    queryKey: ['notificationTemplateBlacklists'],
    queryFn: () => getTemplateBlacklists(),
    staleTime: 1000 * 60 * 60,
  })
}

export const useUserTeamPreferences = () => {
  return useQuery({
    queryKey: ['userTeamPreferences'],
    queryFn: () => getUserTeamPreferences(),
    staleTime: 1000 * 60 * 60,
  })
}

export const useUserTeamAccess = (teamIds: number[]) => {
  return useQuery({
    queryKey: ['userTeamAccess', { teamIds }],
    queryFn: () => getUserTeamAccess(teamIds),
    staleTime: 1000 * 60 * 60,
    enabled: teamIds.length > 0,
  })
}

export const useDiscordChannelDetails = (addresses: string[]) => {
  return useQuery({
    queryKey: ['discordChannelDetails', { addresses }],
    queryFn: () => getDiscordChannelDetails(addresses),
    staleTime: 1000 * 60 * 60,
  })
}

export const useNotifications = (teamId: number) => {
  return useQuery({
    queryKey: ['notifications', { id: teamId }],
    queryFn: () => getNotifications(teamId),
    staleTime: 1000 * 60 * 15,
  })
}

export const useUserCharacterDetails = (enabled: boolean) => {
  return useQuery({
    queryKey: ['userCharacterDetails'],
    queryFn: getPersonalCharactersDetails,
    enabled: enabled,
    staleTime: 1000 * 60 * 5,
  })
}

export const useGuildDetails = (guildId?: number) => {
  const { customDomain } = useCustomDomain()
  const { storeReferences } = useReferenceStore()
  const { getData } = usePrefetchedStore()
  const baseGuildPath = useBasePath('guild')

  const queryFn = async () => {
    const response = guildId
      ? await getGuildDetails(guildId)
      : await getGuildDetailsByPath(baseGuildPath as string)

    storeReferences(response.teamReferences)
    return response
  }

  const initialData =
    customDomain?.guild ?? getData<wowaudit.Guild>(baseGuildPath)

  return useQuery({
    queryKey: ['guild', { path: customDomain?.guild ? '' : baseGuildPath }],
    queryFn,
    staleTime: 1000 * 60 * 60,
    initialData,
  })
}

export const useTeamDetails = (teamId?: number) => {
  const { customDomain } = useCustomDomain()
  const { storeReferences } = useReferenceStore()
  const { getData } = usePrefetchedStore()
  const baseTeamPath = useBasePath('team')

  const queryFn = async () => {
    const response = teamId
      ? await getTeamDetails(teamId)
      : await getTeamDetailsByPath(baseTeamPath as string)

    storeReferences([
      response.ownerReference,
      ...(response.members?.map((member) => member.characterReference) ?? []),
    ])
    return response
  }

  const initialData = customDomain?.team ?? getData<wowaudit.Team>(baseTeamPath)

  return useQuery({
    queryKey: ['team', { path: customDomain?.team ? '' : baseTeamPath }],
    queryFn,
    enabled: !!teamId || !!baseTeamPath,
    staleTime: 1000 * 60 * 60,
    initialData,
  })
}

export const useDiscordReferences = (teamId?: number) => {
  return useQuery({
    queryKey: ['discordReferences', { id: teamId }],
    queryFn: () => (teamId ? getDiscordReferences(teamId) : undefined),
    staleTime: 1000 * 60 * 60,
    enabled: !!teamId,
  })
}

export const useAccessRestrictions = (teamId?: number) => {
  return useQuery({
    queryKey: ['accessRestrictions', { id: teamId }],
    queryFn: () => (teamId ? getAccessRestrictions(teamId) : undefined),
    staleTime: 1000 * 60 * 60,
    enabled: !!teamId,
  })
}

export const useCharacterDetails = (characterId?: number) => {
  return useQuery({
    queryKey: ['characterDetails', { characterId }],
    queryFn: () =>
      characterId ? getCharacterDetails([characterId]) : undefined,
    staleTime: 1000 * 60 * 15,
    enabled: !!characterId,
  })
}

export const useTeamCharacterDetails = (
  teamId?: number,
  ids?: number[],
  key?: string,
) => {
  return useQuery({
    queryKey: ['teamCharacterDetails', { id: teamId, key: key ?? '' }],
    queryFn: () => (teamId && ids ? getCharacterDetails(ids) : undefined),
    staleTime: 1000 * 60 * 15,
    enabled: !!teamId && !!ids,
  })
}

export const useTeamTrackingRules = (teamId?: number) => {
  return useQuery({
    queryKey: ['teamTrackingRules', { id: teamId }],
    queryFn: () => (teamId ? getTeamTrackingRules(teamId) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!teamId,
  })
}

export const useTeamDroptimizerConfigurations = (teamId?: number) => {
  return useQuery({
    queryKey: ['teamDroptimizerConfigurations', { id: teamId }],
    queryFn: () =>
      teamId ? getTeamDroptimizerConfigurations(teamId) : undefined,
    staleTime: 1000 * 60 * 5,
    enabled: !!teamId,
  })
}

export const useGuildMembers = (guildId: number | undefined) => {
  const { storeReferences } = useReferenceStore()

  const queryFn = async () => {
    if (!guildId) return undefined
    const response = await getGuildMembers(guildId)
    storeReferences(response)
    return response
  }

  return useQuery({
    queryKey: ['guildMembers', { id: guildId }],
    queryFn: () => (guildId ? getGuildMembers(guildId) : undefined),
    enabled: !!guildId,
    staleTime: 1000 * 60 * 60,
  })
}

export const useTeamUsers = (teamId?: number) => {
  return useQuery({
    queryKey: ['teamUsers', { id: teamId }],
    queryFn: () => (teamId ? getTeamUsers(teamId) : undefined),
    staleTime: 1000 * 60 * 60,
    enabled: !!teamId,
  })
}

export const useUserApplications = (pendingOnly = false) => {
  const {
    data: { user },
  } = usePageInfo()

  return useQuery({
    queryKey: ['userApplications', { pendingOnly }],
    queryFn: async () => {
      const applications = await getUserApplications(pendingOnly)
      return applications
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 15,
  })
}

export const useTeamApplications = (
  teamId?: number,
  pendingOnly = false,
  hasAccess = true,
) => {
  const { storeReferences } = useReferenceStore()

  return useQuery({
    queryKey: ['teamApplications', { id: teamId, pendingOnly }],
    queryFn: async () => {
      if (!teamId) return undefined
      const applications = await getTeamApplications(teamId, pendingOnly)
      storeReferences(
        applications
          .map((application) => application.characterReference)
          .filter((reference) => reference !== undefined),
      )
      return applications
    },
    staleTime: 1000 * 60 * 15,
    enabled: !!teamId && hasAccess,
  })
}

export const useUserApplicationSummary = () => {
  const {
    data: { user },
  } = usePageInfo()

  return useQuery({
    queryKey: ['userApplicationSummary'],
    queryFn: getUserApplicationSummary,
    staleTime: 1000 * 60 * 15,
    initialData: user?.openApplicationSummary,
    enabled: !!user,
  })
}

export const useApplicationDetails = (teamId?: number, id?: number) => {
  const { storeReferences } = useReferenceStore()
  return useQuery({
    queryKey: ['applicationDetails', { teamId, id }],
    queryFn: async () => {
      if (!id) return undefined
      const application = teamId
        ? await getTeamApplicationDetails(teamId, id)
        : await getUserApplicationDetails(id)
      storeReferences(
        application.alts.concat(
          application.characterReference
            ? [application.characterReference]
            : [],
        ),
      )
      return application
    },
    staleTime: 1000 * 60 * 15,
    enabled: !!id,
  })
}

export const useApplicationMessages = (
  open: boolean,
  type: 'messages' | 'notes',
  teamId?: number,
  id?: number,
) => {
  const { data: applicationDetails } = useApplicationDetails(teamId, id)

  return useQuery({
    queryKey: ['applicationMessages', { teamId, id, type }],
    queryFn: () =>
      teamId && id ? getApplicationMessages(teamId, id, type) : undefined,
    refetchInterval: 1000 * 30,
    initialData: applicationDetails?.messages,
    initialDataUpdatedAt: 0,
    enabled: open && !!teamId && !!id,
  })
}

export const useCharacterWishlist = (
  teamId?: number,
  characterId?: number,
  seasonId?: number,
) => {
  return useQuery({
    queryKey: ['characterWishlist', { teamId, characterId, seasonId }],
    queryFn: () =>
      teamId && characterId && seasonId
        ? getCharacterWishlist(teamId, characterId, seasonId)
        : undefined,
    staleTime: 1000 * 60 * 60,
  })
}

export const useTeamWishlist = (
  teamId: number,
  difficulty: Difficulty,
  seasonId: number,
  configurationId?: number,
) => {
  return useQuery({
    queryKey: [
      'teamWishlist',
      { teamId, difficulty, seasonId, configurationId },
    ],
    queryFn: () =>
      teamId && seasonId
        ? getTeamWishlist(teamId, difficulty, seasonId, configurationId)
        : undefined,
    staleTime: 1000 * 60 * 15,
    enabled: !!teamId && !!seasonId,
  })
}

export const useTeamLootHistory = (options: LootHistoryUrlOptions) => {
  return useQuery({
    queryKey: ['teamLootHistory', options],
    queryFn: () => getTeamLootHistory(options),
    staleTime: 1000 * 60 * 15,
  })
}

export const useUserRaidPlansIndex = (
  seasonId?: number,
  difficulty?: Difficulty,
  enabled = true,
) => {
  const owner = { ownerType: 'user' } as const
  return useQuery({
    queryKey: ['userRaidPlans', { seasonId, difficulty }],
    queryFn: async () =>
      (await getRaidPlansIndex(
        owner,
        seasonId,
        difficulty,
      )) as UserRaidPlansIndex,
    enabled: enabled && !!seasonId,
    staleTime: 1000 * 30,
  })
}

export const useRaidPlans = (
  owner?: RaidPlanOwner,
  seasonId?: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['raidPlans', { owner, seasonId }],
    queryFn: () => (owner ? getRaidPlans(owner, seasonId) : undefined),
    enabled: enabled && !!owner && !!seasonId,
    staleTime: 1000 * 30,
  })
}

export const useRaidPlan = (
  ownerOrTeamId?: RaidPlanOwner | number,
  planId?: number,
) => {
  const owner =
    typeof ownerOrTeamId === 'number'
      ? ({ ownerType: 'team', ownerId: ownerOrTeamId } as const)
      : ownerOrTeamId
  return useQuery({
    queryKey: ['raidPlan', { owner, planId }],
    queryFn: () => (owner && planId ? getRaidPlan(owner, planId) : undefined),
    enabled: !!owner && !!planId,
    staleTime: 1000 * 30,
  })
}

export const useRaidPlanTopExecutionKills = (
  ownerOrTeamId?: RaidPlanOwner | number,
  encounterId?: number,
  enabled = true,
) => {
  const owner =
    typeof ownerOrTeamId === 'number'
      ? ({ ownerType: 'team', ownerId: ownerOrTeamId } as const)
      : ownerOrTeamId
  return useQuery({
    queryKey: ['raidPlanTopExecutionKills', { owner, encounterId }],
    queryFn: () =>
      owner && encounterId
        ? getRaidPlanTopExecutionKills(owner, encounterId)
        : undefined,
    enabled: enabled && !!owner && !!encounterId,
    staleTime: 1000 * 30,
  })
}

export const useTeamAuditData = (teamId?: number, prefetchReady = true) => {
  return useQuery({
    queryKey: ['teamAuditData', { id: teamId }],
    queryFn: () => (teamId ? getAuditData(teamId) : undefined),
    staleTime: 1000 * 60 * 15,
    enabled: !!teamId && prefetchReady,
  })
}

export const useTeamEventStatistics = (
  teamId?: number,
  query?: EventStatisticsQuery,
) => {
  const isRangeQuery = !!query?.from && !!query?.to
  const isSeasonQuery = !!query?.seasonId

  return useQuery({
    queryKey: ['teamEventStatistics', { teamId, query }],
    queryFn: () =>
      teamId && query ? getTeamEventStatistics(teamId, query) : undefined,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: !!teamId && (isRangeQuery || isSeasonQuery),
  })
}

export const useNewsItems = (teamId?: number) => {
  return useQuery({
    queryKey: ['newsItems', { teamId }],
    queryFn: () => (teamId ? getNewsItems(teamId) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!teamId,
  })
}

export const useNewsItem = (teamId?: number, id?: number) => {
  return useQuery({
    queryKey: ['newsItem', { teamId, id }],
    queryFn: () => (teamId && id ? getNewsItem(teamId, id) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!teamId && !!id,
  })
}

export const useTeamRefreshStatus = (teamId?: number) => {
  return useQuery({
    queryKey: ['teamRefreshStatus', { id: teamId }],
    queryFn: () => (teamId ? getTeamRefreshStatus(teamId) : undefined),
    staleTime: 1000 * 10,
    refetchInterval: 1000 * 10,
    enabled: !!teamId,
  })
}

export const useTeamOverviewEvents = (
  teamId?: number,
  options?: {
    limit?: number
    initialCursor?: number
    initialDirection?: 'before' | 'after'
    resetVersion?: number
  },
) => {
  const { storeReferences } = useReferenceStore()
  const limit = options?.limit ?? 15

  return useInfiniteQuery<TeamOverviewEventsResponse>({
    queryKey: [
      'teamEvents',
      {
        id: teamId,
        type: 'overview',
        limit: limit,
        initialCursor: options?.initialCursor,
        initialDirection: options?.initialDirection,
        resetVersion: options?.resetVersion,
      },
    ],
    queryFn: async ({ pageParam }) => {
      if (!teamId) {
        return {
          events: [],
          hasNextPage: false,
          hasPrevPage: false,
        }
      }
      const page = pageParam as
        | { cursor?: number; dir?: 'before' | 'after' }
        | undefined
      const response = await getTeamOverviewEvents(teamId, {
        cursor: page?.cursor,
        dir: page?.dir,
        limit: limit,
      })
      storeReferences(response.events)
      return response
    },
    initialPageParam: {
      cursor: options?.initialCursor,
      dir: options?.initialDirection,
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasNextPage || !lastPage.nextCursor) return undefined
      return { cursor: lastPage.nextCursor, dir: 'after' as const }
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.hasPrevPage || !firstPage.prevCursor) return undefined
      return { cursor: firstPage.prevCursor, dir: 'before' as const }
    },
    maxPages: 1,
    staleTime: 0,
    gcTime: 0,
    placeholderData: (previousData) => previousData,
    refetchOnMount: 'always',
    enabled: !!teamId,
  })
}

export const useTeamCalendarEvents = (teamId?: number, month?: string) => {
  const { storeReferences } = useReferenceStore()

  return useQuery({
    queryKey: ['teamEvents', { id: teamId, type: 'calendar', month }],
    queryFn: async () => {
      if (!teamId || !month) return []
      const events = await getTeamCalendarEvents(teamId, { month })
      storeReferences(events)
      return events
    },
    enabled: !!teamId && !!month,
  })
}

export const useTeamListEvents = (teamId?: number, page = 1) => {
  const { storeReferences } = useReferenceStore()

  return useQuery<TeamListEventsResponse>({
    queryKey: ['teamEvents', { id: teamId, type: 'list', page }],
    queryFn: async () => {
      if (!teamId) {
        return {
          events: [],
          page: 1,
          totalPages: 1,
          totalCount: 0,
        }
      }
      const response = await getTeamListEvents(teamId, { page })
      storeReferences(response.events)
      return response
    },
    placeholderData: keepPreviousData,
    enabled: !!teamId,
  })
}

export const useUserListEvents = (page = 1) => {
  const { storeReferences } = useReferenceStore()
  const {
    data: { user },
  } = usePageInfo()

  return useQuery<TeamListEventsResponse>({
    queryKey: ['userEvents', { type: 'list', page }],
    queryFn: async () => {
      const response = await getUserListEvents({ page })
      storeReferences(response.events)
      return response
    },
    placeholderData: keepPreviousData,
    enabled: !!user,
  })
}

export const useTeamEvent = (
  teamId?: number,
  eventId?: number,
  initialData?: wowaudit.Event | undefined,
) => {
  return useQuery({
    queryKey: ['teamEvent', { teamId, eventId }],
    queryFn: () =>
      teamId && eventId ? getTeamEvent(teamId, eventId) : undefined,
    enabled: !!teamId && !!eventId,
    staleTime: 0,
    initialData,
  })
}

export const useTeamEventChangelog = (
  open: boolean,
  teamId?: number,
  eventId?: number,
) => {
  return useQuery<{ changelog: EventChangelogEntry[] }>({
    queryKey: ['teamEventChangelog', { teamId, eventId }],
    queryFn: () => getTeamEventChangelog(teamId as number, eventId as number),
    enabled: open && !!teamId && !!eventId,
  })
}

export const useEventRaidReports = (
  teamId?: number,
  eventId?: number,
  enabled = false,
) => {
  return useQuery<wowaudit.RaidReport[]>({
    queryKey: ['eventRaidReports', { teamId, eventId }],
    queryFn: () => getEventRaidReports(teamId as number, eventId as number),
    enabled: enabled && !!teamId && !!eventId,
    staleTime: 1000 * 60,
  })
}

export const useTeamEventRaidReportsMap = (teamId?: number, enabled = true) => {
  return useQuery<TeamEventRaidReportsMapResponse>({
    queryKey: ['teamEventRaidReportsMap', { teamId }],
    queryFn: () => getTeamEventRaidReportsMap(teamId as number),
    enabled: enabled && !!teamId,
    staleTime: 1000 * 60,
  })
}

export const useEventActivitiesPlanningInfo = (
  teamId?: number,
  eventId?: number,
  activities: wowaudit.EventActivity[] = [],
  enabled = false,
) => {
  const queryClient = useQueryClient()
  const uniqueActivityKeys = Array.from(
    new Set(
      activities.map((activity) => getEventActivityPlanningKey(activity)),
    ),
  )
  const cachedActivities = uniqueActivityKeys.reduce<
    EventActivitiesPlanningInfo['activities'] | null
  >((acc, activityKey) => {
    if (!teamId || !acc) return null
    const cached = queryClient.getQueryData<
      EventActivitiesPlanningInfo['activities'][string]
    >(['eventActivityPlanningInfo', { teamId, activityKey }])
    if (!cached) return null
    acc[activityKey] = cached
    return acc
  }, {})
  const hasFullCache = uniqueActivityKeys.length > 0 && !!cachedActivities

  return useQuery({
    queryKey: [
      'eventActivitiesPlanningInfo',
      { teamId, eventId, uniqueActivityKeys },
    ],
    queryFn: async () => {
      if (!teamId || !eventId) return undefined
      const response = await getEventActivitiesPlanningInfo(teamId, eventId)
      Object.entries(response.activities).forEach(
        ([activityKey, planningInfo]) => {
          queryClient.setQueryData(
            ['eventActivityPlanningInfo', { teamId, activityKey }],
            planningInfo,
          )
        },
      )
      return response
    },
    initialData: cachedActivities
      ? { activities: cachedActivities }
      : undefined,
    enabled: enabled && !!teamId && !!eventId && !hasFullCache,
    staleTime: 1000 * 60 * 5,
  })
}

export const useOverviewData = (teamId?: number) => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ['overviewData', { id: teamId }],
    queryFn: async () => {
      if (!teamId) return undefined
      const data = await getOverviewData(teamId)

      if (data?.auditData) {
        queryClient.setQueryData(
          ['teamAuditData', { id: teamId }],
          data.auditData,
        )
      }

      return data
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!teamId,
  })
}
